import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiFileText, FiMail, FiCalendar } = FiIcons;

const TermsOfService = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: `By accessing and using aiximo (the "Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.`
    },
    {
      title: "2. Services Description",
      content: `aiximo provides AI-powered software development services including but not limited to:

• Rapid application development
• AI-enhanced code generation and optimization
• Prompt engineering and AI integration
• Code auditing and refinement
• Ongoing maintenance and support

We reserve the right to modify, suspend, or discontinue any part of our services at any time without prior notice.`
    },
    {
      title: "3. User Responsibilities",
      content: `When using our services, you agree to:

• Provide accurate and complete information
• Use our services only for lawful purposes
• Not attempt to gain unauthorized access to our systems
• Respect intellectual property rights
• Maintain the confidentiality of any login credentials
• Not use our services to transmit harmful or malicious content`
    },
    {
      title: "4. Intellectual Property Rights",
      content: `All content, features, and functionality on aiximo are owned by aiximo and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.

For custom development projects:
• You retain ownership of your original business logic and requirements
• We retain ownership of our proprietary development methodologies and tools
• Final code ownership is transferred upon full payment completion
• AI-generated code follows industry-standard licensing practices`
    },
    {
      title: "5. Payment Terms",
      content: `• All fees are quoted in USD and are non-refundable unless otherwise specified
• Payment terms will be specified in individual project agreements
• Late payments may incur additional charges
• We reserve the right to suspend services for overdue accounts
• Refunds are considered on a case-by-case basis for legitimate concerns`
    },
    {
      title: "6. Confidentiality and Data Protection",
      content: `We take your privacy seriously and implement industry-standard security measures:

• All client data is handled in accordance with our Privacy Policy
• We maintain strict confidentiality of all project information
• Data is encrypted both in transit and at rest
• We comply with GDPR, CCPA, and other applicable privacy regulations
• Client data is never shared with third parties without explicit consent`
    },
    {
      title: "7. Service Level Agreement",
      content: `We strive to provide reliable services with:

• 99.9% uptime for hosted applications
• Response time within 24 hours for support requests
• Regular backups and disaster recovery procedures
• Proactive monitoring and maintenance

Service level specifics will be detailed in individual project agreements.`
    },
    {
      title: "8. Limitation of Liability",
      content: `To the fullest extent permitted by law:

• Our total liability shall not exceed the amount paid by you for our services
• We are not liable for indirect, incidental, or consequential damages
• We do not warrant that our services will be uninterrupted or error-free
• You use our services at your own risk
• We are not responsible for third-party integrations or services`
    },
    {
      title: "9. Indemnification",
      content: `You agree to indemnify and hold harmless aiximo from any claims, damages, or expenses arising from:

• Your use of our services
• Your violation of these terms
• Your violation of any third-party rights
• Any content you provide to us`
    },
    {
      title: "10. Termination",
      content: `Either party may terminate services:

• With 30 days written notice for ongoing services
• Immediately for breach of terms
• Upon completion of project deliverables

Upon termination:
• All outstanding fees become immediately due
• We will provide final deliverables as agreed
• Confidentiality obligations remain in effect`
    },
    {
      title: "11. Governing Law",
      content: `These terms are governed by and construed in accordance with the laws of [Your Jurisdiction]. Any disputes shall be resolved through binding arbitration or in the courts of [Your Jurisdiction].`
    },
    {
      title: "12. Changes to Terms",
      content: `We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Your continued use of our services constitutes acceptance of any changes. We will notify users of significant changes via email or website notice.`
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center mb-6">
              <SafeIcon icon={FiFileText} className="h-12 w-12 text-blue-600 mr-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                Terms of Service
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-4">
              Please read these terms carefully before using our services
            </p>
            <div className="flex items-center justify-center text-sm text-gray-500">
              <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
              <span>Last updated: December 2024</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-12">
              <div className="flex items-start">
                <SafeIcon icon={FiShield} className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Important Notice</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    These Terms of Service constitute a legally binding agreement between you and aiximo. 
                    By using our services, you acknowledge that you have read, understood, and agree to be bound by these terms.
                  </p>
                </div>
              </div>
            </div>

            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="mb-12"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gray-50 border border-gray-200 rounded-2xl p-8 mt-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
              <div className="space-y-4">
                <p className="text-gray-700">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="flex items-center text-gray-700">
                  <SafeIcon icon={FiMail} className="h-5 w-5 mr-3 text-blue-600" />
                  <a href="mailto:legal@aiximo.com" className="text-blue-600 hover:text-blue-800 transition-colors">
                    legal@aiximo.com
                  </a>
                </div>
                <div className="text-gray-700">
                  <strong>aiximo</strong><br />
                  AI-Powered Development Agency<br />
                  Terms of Service Department
                </div>
              </div>
            </motion.div>

            {/* Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200"
            >
              <Link
                to="/privacy-policy"
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center"
              >
                <SafeIcon icon={FiShield} className="h-4 w-4 mr-2" />
                Privacy Policy
              </Link>
              <Link
                to="/contact"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300"
              >
                Contact Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;